import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { ArrowLeftIcon, PhotoIcon } from '@heroicons/react/24/outline';
import { createProduct, fetchProductById, updateProduct } from './index';
import Spinner from '../components/Spinner';

const AddProduct = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'Vegetables',
    price: '',
    unit: 'basket',
    stock: '',
    origin: 'Local',
    imageURL: '',
    isFeatured: false
  });
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (isEditMode && id) {
      const loadProduct = async () => {
        try {
          const { data } = await fetchProductById(id);
          setFormData({
            name: data.name,
            description: data.description,
            category: data.category,
            price: data.price,
            unit: data.unit,
            stock: data.stock,
            origin: data.origin,
            imageURL: data.imageURL,
            isFeatured: data.isFeatured || false
          });
        } catch (error) {
          console.error("Failed to fetch product", error);
          alert("Failed to load product details.");
          navigate('/admin');
        }
      };
      loadProduct();
    }
  }, [id, isEditMode, navigate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    if (name === 'isFeatured') {
       setFormData({ ...formData, [name]: (e.target as HTMLInputElement).checked });
    } else {
       setFormData({ ...formData, [name]: value });
    }
  };

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setUploading(true);

      // Cloudinary Configuration
      const cloudName = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || "dkkm3uigr";
      const uploadPreset = process.env.REACT_APP_CLOUDINARY_PRESET || "kano_fresh_preset";

      const data = new FormData();
      data.append("file", file);
      data.append("upload_preset", uploadPreset);

      try {
        const res = await fetch(`https://api.cloudinary.com/v1_1/${cloudName}/image/upload`, {
          method: "POST",
          body: data
        });
        const uploadedImage = await res.json();
        setFormData({ ...formData, imageURL: uploadedImage.secure_url });
      } catch (err) {
        console.error("Upload failed", err);
        alert("Failed to upload image. Please check your internet connection.");
      } finally {
        setUploading(false);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    // Prepare payload with correct data types
    const payload = {
      ...formData,
      price: Number(formData.price),
      stock: Number(formData.stock)
    };

    try {
      if (isEditMode && id) {
        await updateProduct(id, payload);
        alert('Product updated successfully!');
      } else {
        await createProduct(payload);
        alert('Product added successfully!');
      }
      navigate('/admin');
    } catch (error) {
      console.error('Failed to save product', error);
      alert(`Failed to ${isEditMode ? 'update' : 'add'} product. Please try again.`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-secondary">
      <Navbar />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
        {/* Header with Back Button */}
        <div className="mb-8 flex items-center">
          <Link to="/admin" className="mr-4 text-gray-500 hover:text-primary transition">
            <ArrowLeftIcon className="h-6 w-6" />
          </Link>
          <div>
            <h1 className="text-2xl sm:text-3xl font-serif font-bold text-primary">{isEditMode ? 'Edit Product' : 'Add New Product'}</h1>
            <p className="mt-1 text-sm text-gray-600 font-sans">{isEditMode ? 'Update product details.' : 'Add fresh produce to the inventory.'}</p>
          </div>
        </div>

        <div className="bg-white shadow rounded-lg overflow-hidden border border-gray-100 p-6 sm:p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            
            {/* Name */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 font-sans">Product Name</label>
              <div className="mt-1">
                <input type="text" name="name" id="name" required value={formData.name} onChange={handleChange} className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary sm:text-sm" placeholder="e.g. Fresh Spinach" />
              </div>
            </div>

            {/* Description */}
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 font-sans">Description</label>
              <div className="mt-1">
                <textarea name="description" id="description" rows={3} required value={formData.description} onChange={handleChange} className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary sm:text-sm" placeholder="Describe the produce..." />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2">
              {/* Category */}
              <div>
                <label htmlFor="category" className="block text-sm font-medium text-gray-700 font-sans">Category</label>
                <div className="mt-1">
                  <select name="category" id="category" required value={formData.category} onChange={handleChange} className="block w-full px-3 py-2 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary sm:text-sm">
                    <option value="Vegetables">Vegetables</option>
                    <option value="Fruits">Fruits</option>
                  </select>
                </div>
              </div>

              {/* Unit */}
              <div>
                <label htmlFor="unit" className="block text-sm font-medium text-gray-700 font-sans">Unit</label>
                <div className="mt-1">
                  <select name="unit" id="unit" required value={formData.unit} onChange={handleChange} className="block w-full px-3 py-2 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary sm:text-sm">
                    <option value="basket">Basket</option>
                    <option value="kg">Kg</option>
                    <option value="pack">Pack</option>
                    <option value="piece">Piece</option>
                    <option value="bag">Bag</option>
                  </select>
                </div>
              </div>

              {/* Origin */}
              <div>
                <label htmlFor="origin" className="block text-sm font-medium text-gray-700 font-sans">Origin</label>
                <div className="mt-1">
                  <select name="origin" id="origin" required value={formData.origin} onChange={handleChange} className="block w-full px-3 py-2 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary sm:text-sm">
                    <option value="Local">Local</option>
                    <option value="Imported">Imported</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2">
              {/* Price */}
              <div>
                <label htmlFor="price" className="block text-sm font-medium text-gray-700 font-sans">Price (₦)</label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span className="text-gray-500 sm:text-sm">₦</span>
                  </div>
                  <input type="number" name="price" id="price" required value={formData.price} onChange={handleChange} className="focus:ring-primary focus:border-primary block w-full pl-7 pr-12 sm:text-sm border-gray-300 rounded-md" placeholder="0.00" />
                </div>
              </div>

              {/* Stock */}
              <div>
                <label htmlFor="stock" className="block text-sm font-medium text-gray-700 font-sans">Stock Quantity</label>
                <div className="mt-1">
                  <input type="number" name="stock" id="stock" required value={formData.stock} onChange={handleChange} className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary sm:text-sm" placeholder="0" />
                </div>
              </div>
            </div>

            {/* Image Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 font-sans">Product Image</label>
              <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                <div className="space-y-1 text-center">
                  {formData.imageURL ? (
                    <img src={formData.imageURL} alt="Preview" className="mx-auto h-48 object-cover rounded-md mb-4" />
                  ) : (
                    <PhotoIcon className="mx-auto h-12 w-12 text-gray-400" />
                  )}
                  <div className="flex text-sm text-gray-600 justify-center">
                    <label htmlFor="file-upload" className="relative cursor-pointer bg-white rounded-md font-medium text-primary hover:text-green-800 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary">
                      <span>{formData.imageURL ? 'Change file' : 'Upload a file'}</span>
                      <input id="file-upload" name="imageURL" type="file" className="sr-only" accept="image/*" onChange={handleImageChange} />
                    </label>
                  </div>
                  <p className="text-xs text-gray-500">PNG, JPG, GIF up to 10MB</p>
                </div>
              </div>
            </div>

            {/* Is Featured */}
            <div className="flex items-start">
              <div className="flex items-center h-5">
                <input id="isFeatured" name="isFeatured" type="checkbox" checked={formData.isFeatured} onChange={handleChange} className="focus:ring-primary h-4 w-4 text-primary border-gray-300 rounded" />
              </div>
              <div className="ml-3 text-sm">
                <label htmlFor="isFeatured" className="font-medium text-gray-700 font-sans">Feature this product</label>
                <p className="text-gray-500 font-sans">Featured products appear on the homepage.</p>
              </div>
            </div>

            {/* Submit Button */}
            <div className="pt-4">
              <button type="submit" disabled={loading || uploading} className="w-full flex justify-center items-center gap-2 py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-green-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition duration-150 disabled:opacity-50">
                {loading ? <><Spinner /> Saving...</> : uploading ? <><Spinner /> Uploading Image...</> : (isEditMode ? 'Update Product' : 'Save Product')}
              </button>
            </div>

          </form>
        </div>
      </div>
    </div>
  );
};

export default AddProduct;